import React, { useState, useEffect } from 'react';
import BackButton from '../../../ui/BackButton';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableRow,
  Paper,
  styled,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Search as SearchIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const StudentList = () => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewStudentModal, setShowViewStudentModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState({ studentId: null, studentName: '' });
  const [importFile, setImportFile] = useState(null);

  const navigate = useNavigate();


  // Form states for view modal
  const [formData, setFormData] = useState({
    admissionNo: '',
    studentName: '',
    fatherName: '',
    qualification: '',
    address: '',
    dateOfAdmission: '',
    mobileNo: '',
    email: '',
    dateOfBirth: '',
    course: '',
    status: '',
  });

 
 
 const [studentData, setStudentData] = useState([]);
const [filteredData, setFilteredData] = useState([]);

  const handleView = (student) => {
    setSelectedStudent({ studentId: student.studentId, studentName: student.studentName });
    setFormData({
      admissionNo: student.admissionNo,
      studentName: student.studentName,
      fatherName: student.fatherName,
      qualification: student.qualification,
      address: student.address,
      dateOfAdmission: student.dateOfAdmission,
      mobileNo: student.mobileNo,
      email: student.email,
      dateOfBirth: student.dateOfBirth,
      course: student.course,
      status: student.status,
    });
    setShowViewStudentModal(true);
  };

  const handleShowDeleteModal = (studentId, studentName) => {
    setSelectedStudent({ studentId, studentName });
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    setStudentData((prevStudents) =>
      prevStudents.filter((student) => student.studentId !== selectedStudent.studentId)
    );
    setSnackbarMessage(`${selectedStudent.studentName} deleted successfully!`);
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
    setShowDeleteModal(false);
    setShowSuccessModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };

  const handleImport = () => {
    if (importFile) {
      // Placeholder for file import logic
      setSnackbarMessage('File imported successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setShowImportModal(false);
      setImportFile(null);
    } else {
      setSnackbarMessage('Please select a file to import');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  useEffect(() => {
    setFilteredData(
      studentData.filter((student) =>
        student.studentName.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, studentData]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <BackButton />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search by Candidate Name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    color: theme.palette.primary.main,
                    marginLeft: 1.5,
                    fontSize: 22,
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{
                    color: "grey",
                    marginRight: 1,
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: '30px',
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(255, 255, 255, 1)',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                : '0 2px 8px rgba(0, 0, 0, 0.1)',
              '& .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
              },
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.2)'
                  : 'rgba(245, 245, 245, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                  : '0 2px 12px rgba(0, 0, 0, 0.15)',
              },
              '&.Mui-focused': {
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.25)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `2px solid ${theme.palette.primary.main}`,
                },
              },
              paddingLeft: 1.5,
              height: '50px',
              transition: 'all 0.3s ease',
            },
          }}
          sx={{
            width: 300,
            maxWidth: '100%',
            '& .MuiInputBase-input': {
              padding: '10px 14px',
              color: theme.palette.text.primary,
              fontWeight: 500,
            },
          }}
        />
       <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0, // Remove MUI's default padding
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

     <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                minWidth: 1200,
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Admission No.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Student Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Mobile No.</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Email Id</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Admission Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((student, index) => (
                    <TableRow
                      key={student.studentId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{student.admissionNo}</TableCell>
                      <TableCell sx={{ fontSize: '1.1rem' }}>{student.studentName}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{student.mobileNo}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{student.email}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{student.dateOfAdmission}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{student.status}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View student" arrow>
                          <IconButton
                            onClick={() => navigate(`/view-student/${student.studentId}`)}
                            sx={{
                              color: theme.palette.primary.light,
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete student" arrow>
                          <IconButton
                            color="error"
                            onClick={() => handleShowDeleteModal(student.studentId, student.studentName)}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}>
                    <TableCell colSpan={8} align="center" sx={{ fontSize: '1rem', padding: '20px',wordSpacing:7}}>
                      No Search Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedStudent.studentName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* View Student Modal */}
      <Modal
        open={showViewStudentModal}
        onClose={() => setShowViewStudentModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              View Student
            </Typography>
            <IconButton onClick={() => setShowViewStudentModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Admission No."
                  variant="outlined"
                  fullWidth
                  value={formData.admissionNo}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Student Name"
                  variant="outlined"
                  fullWidth
                  value={formData.studentName}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Father's Name"
                  variant="outlined"
                  fullWidth
                  value={formData.fatherName}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Qualification"
                  variant="outlined"
                  fullWidth
                  value={formData.qualification}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Address"
                  variant="outlined"
                  fullWidth
                  value={formData.address}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Date of Admission"
                  variant="outlined"
                  fullWidth
                  value={formData.dateOfAdmission}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Mobile No."
                  variant="outlined"
                  fullWidth
                  value={formData.mobileNo}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Email Id"
                  variant="outlined"
                  fullWidth
                  value={formData.email}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Date of Birth"
                  variant="outlined"
                  fullWidth
                  value={formData.dateOfBirth}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
                <TextField
                  label="Course"
                  variant="outlined"
                  fullWidth
                  value={formData.course}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Status"
                  variant="outlined"
                  fullWidth
                  value={formData.status}
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: theme.palette.text.primary,
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setShowViewStudentModal(false)}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                >
                  CLOSE
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Import Modal */}
      <Modal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '80vh',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Import Students
            </Typography>
            <IconButton onClick={() => setShowImportModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ maxHeight: 'calc(80vh - 64px)', overflowY: 'auto', padding: '20px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                component="label"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark },
                }}
              >
                Select File
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              {importFile && (
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                  Selected: {importFile.name}
                </Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleImport}
                  sx={{
                    mr: 2,
                    backgroundColor: theme.palette.primary.main,
                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                  }}
                  disabled={!importFile}
                >
                  IMPORT
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setShowImportModal(false)}
                >
                  CANCEL
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Student Deleted ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Student Deleted Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal
  open={showUserGuideModal}
  onClose={() => setShowUserGuideModal(false)}
  BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 800,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
        Student List Module - User Guide
      </Typography>
      <IconButton
        onClick={() => setShowUserGuideModal(false)}
        sx={{ color: '#fff', position: 'absolute', right: 14 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Scrollable Content */}
   <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Search Students */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow these steps to search for a student:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Use the search bar at the top left of the Student List.</li>
    <li>Enter the student's name or part of it to filter the table.</li>
    <li>The table will update automatically to show matching students.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Search is case-insensitive and filters by Student Name.
  </Typography>

  {/* Section 2: View Student Details */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to view student details:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Select the student from the Student List table.</li>
    <li>Click the <strong>View</strong> (eye icon) in the Action column to open the detailed view.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Clicking the View icon will open a detailed student profile page.
  </Typography>

  {/* Section 3: Delete a Student */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete a student:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Select the student you want to delete from the Student List table.</li>
    <li>Click the <strong>Delete</strong> (trash icon) in the Action column.</li>
    <li>Confirm deletion in the modal by clicking <strong>Yes, Delete</strong>.</li>
    <li>The student will be removed, and a success message will appear.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted student records cannot be recovered. Proceed with caution.
  </Typography>

  {/* Section 4: Import Students */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to import student data:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Click the <strong>IMPORT</strong> button to open the import modal.</li>
    <li>Click <strong>Select File</strong> to choose a file from your device.</li>
    <li>Ensure the file matches the required format (e.g., CSV with correct headers).</li>
    <li>Click <strong>IMPORT</strong> to upload the file.</li>
    <li>A success message will confirm the import, or an error will display if the file is invalid.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Verify the file format to avoid import errors.
  </Typography>

  {/* Section 5: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the student list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Use the pagination controls at the bottom to browse pages.</li>
    <li>Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to switch pages.</li>
    <li>Select a specific page number by clicking on it.</li>
    <li>Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>

  {/* Bottom Close Button */}
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
    {/* Close button can be added here if needed */}
  </Box>
</CustomTableScrollbar>

  </Box>
</Modal>
    </Container>
  );
};

export default StudentList;